import {
  MOVE_ASSOCIATE,
  SET_MODEL,
  RESOURCE_STATUS_ENRICHMENT,
  RESET_MODEL,
  SET_SLOT_LABEL
} from '../action/ShiftAllocationAction';
import { cloneDeep } from 'lodash';
import { ALLOCATION_TYPE, DEFAULT_SHIFT_ALLOCATION_MODEL, ThumbnailBadge } from '../data';

export const ShiftAllocationReducer = (state, action) => {
  switch (action.type) {
    case MOVE_ASSOCIATE:
      return moveAssociate(state, action);
    case SET_SLOT_LABEL:
      return setSlotLabel(state, action);
    case SET_MODEL:
      // do any transformation here.
      return { ...state, ...action.payload };
    case RESOURCE_STATUS_ENRICHMENT:
      return enrichAssociates(state, action);
    case RESET_MODEL:
      return { ...DEFAULT_SHIFT_ALLOCATION_MODEL, ...action.payload };
    default:
      return state;
  }
};

// Sets (or clears) a free-text label for a single slot on a board entry. Slots are identified by a
// stable slotId (e.g. "<boardEntryId>::<process>::<index>") so the label stays with the position,
// not the associate. An empty/blank label clears the override (slot reverts to its default header).
// Updates both processInfo (used for rendering) and boardEntries (what the save payload is built
// from) so the value is ready to persist once backend support lands.
const setSlotLabel = (state, action) => {
  const { boardEntryId, slotId, label } = action.data;
  if (!boardEntryId || !slotId) {
    return state;
  }
  const stateCopy = cloneDeep(state);
  const trimmed = (label || '').trim();

  const applyToEntry = (entry) => {
    if (!entry || entry.id !== boardEntryId) {
      return;
    }
    const slotLabels = { ...(entry.slotLabels || {}) };
    if (trimmed === '') {
      delete slotLabels[slotId];
    } else {
      slotLabels[slotId] = trimmed;
    }
    entry.slotLabels = slotLabels;
  };

  (stateCopy.processInfo || []).forEach(applyToEntry);
  (stateCopy.boardEntries || []).forEach(applyToEntry);

  return stateCopy;
};

const aisleOrderFn = (assignment1, assignment2) => {
  const {
    allocationAttrs: { aisles: aisles1 },
    resourceId: associateAlias1
  } = assignment1;
  const {
    allocationAttrs: { aisles: aisles2 },
    resourceId: associateAlias2
  } = assignment2;

  // Guard "empty aisle" cards (aisles null/empty) so sorting never throws; they sort last, then by alias.
  const hasAisles1 = Array.isArray(aisles1) && aisles1.length > 0;
  const hasAisles2 = Array.isArray(aisles2) && aisles2.length > 0;

  if (!hasAisles1 || !hasAisles2) {
    if (hasAisles1 === hasAisles2) {
      return String(associateAlias1).localeCompare(String(associateAlias2));
    }
    return hasAisles1 ? -1 : 1;
  }

  if (Number(aisles1[0]) === Number(aisles2[0])) {
    return String(associateAlias1).localeCompare(String(associateAlias2));
  }

  return Number(aisles1[0]) - Number(aisles2[0]);
};

const enrichAssociates = (state, action) => {
  const stateCopy = cloneDeep(state);
  const { resourceStatuses } = action.payload;

  resourceStatuses.forEach((resourceStatus) => {
    const { punchedInStatus, resourceId, trackingProcess, productivity } = resourceStatus;
    const associate = stateCopy.associateInfo[resourceId];
    let totalUnitsProcesses = 0;
    if (associate) {
      associate.clockedInStatus = punchedInStatus;
      if (productivity) {
        Object.values(productivity).forEach((processPath) => {
          totalUnitsProcesses += processPath.unitsProcesses;
        });
        if (totalUnitsProcesses === 0 && associate.clockedInStatus) {
          stateCopy.associateInfo[resourceId].thumbnailBadge = new ThumbnailBadge('error', '!');
        }
      }
      if (!stateCopy.associateInfo[resourceId].trackingPath && trackingProcess) {
        stateCopy.associateInfo[resourceId].trackingPath = trackingProcess;
      }
    }
  });

  return stateCopy;
};

const moveAssociate = (state, action) => {
  const stateCopy = cloneDeep(state);
  const { associateAlias } = action.data;
  const { boardEntry, processPath } = action.data.to;
  const fromBoardEntry = action.data.from && action.data.from.boardEntry;
  // Same-entry re-assign must replace, not duplicate: drop the associate's existing card here
  // (see the removeAssociate skip below).
  const isSameBoardEntry = fromBoardEntry === boardEntry;
  const toBoardEntry = stateCopy.processInfo.find((entry) => {
    return entry.id === boardEntry;
  });
  const assignments = toBoardEntry.assignments.filter((assignment) => assignment.resourceId !== associateAlias);
  stateCopy.associateInfo[associateAlias].thumbnailBadge = null;

  const processPathEntry = toBoardEntry.processPathEntries.find((entry) => entry.process === processPath);
  if (toBoardEntry.allocationType === ALLOCATION_TYPE.AISLES) {
    const { minAisle, maxAisle } = action.data.to;
    if (isNaN(minAisle) || isNaN(maxAisle) || minAisle > maxAisle) {
      return state;
    }
    const clusterId = boardEntry.split('_')[1];
    const newAssignment = {
      resourceId: associateAlias,
      boardEntity: boardEntry,
      allocationAttrs: {
        process: processPath,
        cluster: clusterId,
        type: ALLOCATION_TYPE.AISLES,
        aisles: Array(maxAisle - minAisle + 1)
          .fill()
          .map((_, idx) => `${clusterId}-${minAisle + idx}`)
      }
    };

    if (processPathEntry) {
      newAssignment.allocationAttrs = {
        ...newAssignment.allocationAttrs,
        calmCode: processPathEntry.calmCode,
        pathType: processPathEntry.pathType
      };
    }
    assignments.push(newAssignment);
    assignments.sort(aisleOrderFn);
    toBoardEntry.assignments = assignments;
  } else {
    const newAssignment = {
      resourceId: associateAlias,
      boardEntity: boardEntry,
      allocationAttrs: {
        process: processPath
      }
    };
    if (processPathEntry) {
      newAssignment.allocationAttrs = {
        ...newAssignment.allocationAttrs,
        calmCode: processPathEntry.calmCode,
        pathType: processPathEntry.pathType
      };
    }
    assignments.push(newAssignment);
    toBoardEntry.assignments = assignments;
  }
  // Same-entry: old card already dropped above. Cross-entry: clear it from the source.
  return isSameBoardEntry ? stateCopy : removeAssociate(stateCopy, action);
};

const removeAssociate = (state, action) => {
  const stateCopy = cloneDeep(state);
  const { associateAlias } = action.data;
  const { boardEntry } = action.data.from;

  const fromBoardEntry = stateCopy.processInfo.find((entry) => entry.id === boardEntry);

  const assignments = fromBoardEntry.assignments;
  const indexOfItemToRemove = assignments.findIndex((assignment) => assignment.resourceId === associateAlias);
  if (indexOfItemToRemove > -1) {
    assignments.splice(indexOfItemToRemove, 1);
  }

  return stateCopy;
};
